import { useState } from 'react';
import { C } from '../styles/colours.js';
import { supabase } from '../supabase.js';

function Login({ onBack, onLogin, onSignup }) {
  const [mode, setMode] = useState('login'); // 'login' | 'forgot' | 'sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [needsConfirm, setNeedsConfirm] = useState(false);

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10, padding: '12px 14px',
    fontSize: 14, color: C.white,
    outline: 'none', fontFamily: 'sans-serif',
    boxSizing: 'border-box',
  };

  const friendlyError = (msg) => {
    if (!msg) return 'Something went wrong. Please try again.';
    const m = msg.toLowerCase();
    if (m.includes('email not confirmed')) return 'Please confirm your email first — check your inbox (and spam) for the confirmation link.';
    if (m.includes('invalid login credentials')) return 'Wrong email or password. Please try again.';
    return msg;
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) { setError('Please enter your email and password'); return; }
    setLoading(true); setError(''); setInfo(''); setNeedsConfirm(false);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) {
      setError(friendlyError(err.message));
      if ((err.message || '').toLowerCase().includes('email not confirmed')) setNeedsConfirm(true);
    } else {
      onLogin(data.user);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) { setError('Please enter your email first'); return; }
    setLoading(true); setError(''); setInfo('');
    const { error: err } = await supabase.auth.resend({ type: 'signup', email, options: { emailRedirectTo: window.location.origin } });
    setLoading(false);
    if (err) setError(friendlyError(err.message));
    else { setInfo('📭 Confirmation email sent again — check your inbox (and spam).'); setNeedsConfirm(false); }
  };

  const handleForgot = async () => {
    if (!email.trim()) { setError('Please enter your email first'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    setLoading(false);
    if (err) setError(friendlyError(err.message));
    else setMode('sent');
  };

  const header = (backAction, backText) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
      <button onClick={backAction} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>{backText}</button>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, color: C.white }}>TQ</div>
    </div>
  );

  const pageStyle = { background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' };

  // ---- RESET LINK SENT ----
  if (mode === 'sent') {
    return (
      <div style={pageStyle}>
        {header(() => { setMode('login'); setError(''); }, '← Back to login')}
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>📭</div>
          <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 12 }}>Check your inbox!</div>
          <div style={{ fontSize: 14, color: C.textSub, lineHeight: 1.6, marginBottom: 6 }}>We sent a password reset link to:</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.gold, marginBottom: 16, wordBreak: 'break-all' }}>{email}</div>
          <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6, marginBottom: 24 }}>Click the link to set a new password. Don't see it? <strong style={{ color: C.white }}>Check your spam / junk folder.</strong></div>
          <button onClick={() => { setMode('login'); setError(''); }} style={{ width: '100%', background: C.gold, color: '#000', border: 'none', borderRadius: 14, padding: 16, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>Back to Login</button>
        </div>
      </div>
    );
  }

  // ---- FORGOT PASSWORD ----
  if (mode === 'forgot') {
    return (
      <div style={pageStyle}>
        {header(() => { setMode('login'); setError(''); }, '← Back')}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 4 }}>Reset password 🔑</div>
          <div style={{ fontSize: 13, color: C.textSub }}>Enter your email and we'll send you a reset link.</div>
        </div>
        {error && (<div style={{ background: `${C.red}20`, border: `1px solid ${C.red}40`, borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: C.red }}>{error}</div>)}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>EMAIL</div>
          <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
        </div>
        <button onClick={handleForgot} disabled={loading} style={{ width: '100%', background: loading ? 'rgba(255,255,255,0.1)' : C.gold, color: loading ? C.textMuted : '#000', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: 20 }}>
          {loading ? 'Sending...' : 'Send Reset Link'}
        </button>
        <div style={{ textAlign: 'center', fontSize: 13, color: C.textSub }}>
          Remembered it?{' '}
          <span onClick={() => { setMode('login'); setError(''); }} style={{ color: C.gold, fontWeight: 700, cursor: 'pointer' }}>Log in</span>
        </div>
      </div>
    );
  }

  // ---- LOGIN ----
  return (
    <div style={pageStyle}>
      {header(onBack, '← Back')}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 4 }}>Welcome back 👋</div>
        <div style={{ fontSize: 13, color: C.textSub }}>Log in to your host account</div>
      </div>

      {error && (
        <div style={{ background: `${C.red}20`, border: `1px solid ${C.red}40`, borderRadius: 10, padding: '12px 14px', marginBottom: needsConfirm ? 8 : 16, fontSize: 13, color: C.red }}>
          {error}
        </div>
      )}
      {needsConfirm && (
        <div onClick={handleResend} style={{ fontSize: 12, color: C.gold, fontWeight: 700, marginBottom: 16, cursor: 'pointer' }}>
          📨 Resend confirmation email
        </div>
      )}
      {info && (
        <div style={{ background: `${C.green}20`, border: `1px solid ${C.green}40`, borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: C.green }}>
          {info}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>EMAIL</div>
        <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>PASSWORD</div>
        <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
      </div>

      <div onClick={() => { setMode('forgot'); setError(''); setInfo(''); setNeedsConfirm(false); }} style={{ textAlign: 'right', fontSize: 11, color: C.gold, marginBottom: 24, cursor: 'pointer' }}>
        Forgot password?
      </div>

      <button onClick={handleLogin} disabled={loading} style={{ width: '100%', background: loading ? 'rgba(255,255,255,0.1)' : C.gold, color: loading ? C.textMuted : '#000', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: 28 }}>
        {loading ? 'Logging in...' : 'Log In'}
      </button>

      <div style={{ textAlign: 'center', fontSize: 13, color: C.textSub }}>
        No account yet?{' '}
        <span onClick={onSignup} style={{ color: C.gold, fontWeight: 700, cursor: 'pointer' }}>Sign up free</span>
      </div>
    </div>
  );
}

export default Login;